import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaCheck, FaEdit, FaTimes } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExportOptions from "../../ExportOptions";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import TableSkeleton from "../../TableSkeleton";
import Pagination from "../../Shared/Pagination";

const API_BASE = "https://api.muktihospital.com/api/pharmacy-category";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const AddCategoryModal = ({ isOpen, onClose, onSave }) => {
  const [categoryData, setCategoryData] = useState({ categoryNames: [""] });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCategoryData({ categoryNames: [""] });
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const checkDuplicateName = async (categoryName) => {
    try {
      const response = await axios.get(`${API_BASE}`, {
        headers: { "x-api-key": API_KEY },
      });
      const existingCategories = response.data.categories || [];
      return existingCategories.some(
        (category) => category.name.toLowerCase() === categoryName.toLowerCase()
      );
    } catch (error) {
      console.error("Error checking duplicate name:", error);
      return false; // Assume no duplicate in case of error to avoid blocking save
    }
  };

  const validateForm = async () => {
    const newErrors = {};
    let hasError = false;

    for (let index = 0; index < categoryData.categoryNames.length; index++) {
      const categoryName = categoryData.categoryNames[index];
      if (!categoryName.trim()) {
        newErrors[`categoryName${index}`] = `Category Name ${index + 1} is required`;
        hasError = true;
      } else {
        const isDuplicate = await checkDuplicateName(categoryName.trim());
        if (isDuplicate) {
          newErrors[`categoryName${index}`] = `Category Name "${categoryName.trim()}" already exists`;
          hasError = true;
        }
      }
    }

    setErrors(newErrors);
    if (hasError) {
      toast.error("Please fix the errors in the form", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return !hasError;
  };

  const handleCategoryNameChange = (index, value) => {
    const updatedCategoryNames = [...categoryData.categoryNames];
    updatedCategoryNames[index] = value;
    setCategoryData((prevData) => ({
      ...prevData,
      categoryNames: updatedCategoryNames,
    }));
    if (value.trim()) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`categoryName${index}`];
        return newErrors;
      });
    }
  };

  const addCategoryNameField = () => {
    setCategoryData((prevData) => ({
      ...prevData,
      categoryNames: [...prevData.categoryNames, ""],
    }));
  };

  const removeCategoryNameField = (index) => {
    setCategoryData((prevData) => ({
      ...prevData,
      categoryNames: prevData.categoryNames.filter((_, i) => i !== index),
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`categoryName${index}`];
      return newErrors;
    });
  };

  const handleSave = async () => {
    if (!(await validateForm())) return;

    setIsLoading(true);
    try {
      const promises = categoryData.categoryNames.map((categoryName) =>
        axios.post(
          `${API_BASE}/add`,
          { name: categoryName.trim() },
          {
            headers: {
              "x-api-key": API_KEY,
              "Content-Type": "application/json",
            },
          }
        )
      );

      const results = await Promise.allSettled(promises);
      const newCategories = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled" && result.value.status === 201) {
          newCategories.push(result.value.data.category);
        } else {
          const errorMessage =
            result.reason?.response?.data?.error ||
            `Failed to add category ${categoryData.categoryNames[index]}!`;
          toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
          });
        }
      });

      if (newCategories.length > 0) {
        toast.success(
          `${newCategories.length} category(s) added successfully!`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
        onSave(newCategories);
        setCategoryData({ categoryNames: [""] });
        setErrors({});
        onClose();
      } else if (results.every((result) => result.status === "rejected")) {
        toast.error("Failed to add any categories!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Network error! Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-700">Add Medicine Category</h2>
        </div>
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category Name <span className="text-red-500">*</span>
          </label>
          {categoryData.categoryNames.map((categoryName, index) => (
            <div key={index} className="grid grid-cols-5 gap-2 mb-4">
              <div className="col-span-4">
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => handleCategoryNameChange(index, e.target.value)}
                  className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                    errors[`categoryName${index}`] ? "border-red-500" : ""
                  }`}
                  placeholder="Category Name"
                  disabled={isLoading}
                />
                {errors[`categoryName${index}`] && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors[`categoryName${index}`]}
                  </span>
                )}
              </div>
              {categoryData.categoryNames.length > 1 && (
                <div className="col-span-1 flex items-end">
                  <button
                    onClick={() => removeCategoryNameField(index)}
                    className="p-2 text-red-500 hover:text-red-700"
                    disabled={isLoading}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            onClick={addCategoryNameField}
            className="flex items-center mt-3 gap-1 bg-M-primary-color/90 text-sm text-white py-1 px-3 rounded-sm hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            Add
          </button>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditCategoryModal = ({ isOpen, onClose, onSave, category }) => {
  const [categoryName, setCategoryName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (category) setCategoryName(category.categoryName || "");
  }, [category]);

  if (!isOpen) return null;

  const checkDuplicateName = async (categoryName, currentId) => {
    try {
      const response = await axios.get(`${API_BASE}`, {
        headers: { "x-api-key": API_KEY },
      });
      const existingCategories = response.data.categories || [];
      return existingCategories.some(
        (cat) =>
          cat.name.toLowerCase() === categoryName.toLowerCase() &&
          cat.id !== currentId
      );
    } catch (error) {
      console.error("Error checking duplicate name:", error);
      return false; // Assume no duplicate in case of error to avoid blocking save
    }
  };

  const handleSave = async () => {
    if (!categoryName.trim()) {
      setError("Category Name is required");
      toast.error("Category Name is required!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const isDuplicate = await checkDuplicateName(categoryName.trim(), category.id);
    if (isDuplicate) {
      setError(`Category Name "${categoryName.trim()}" already exists`);
      toast.error(`Category Name "${categoryName.trim()}" already exists`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.put(
        `${API_BASE}/edit/${category.id}`,
        { name: categoryName.trim() },
        {
          headers: {
            "x-api-key": API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Category updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        onSave(category.id, categoryName);
        onClose();
      } else {
        toast.error("Failed to update category!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Network error! Please try again.";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Edit Medicine Category</h2>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => {
                setCategoryName(e.target.value);
                setError("");
              }}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                error ? "border-red-500" : ""
              }`}
              placeholder="Category Name"
              disabled={isLoading}
            />
            {error && (
              <span className="text-red-500 text-xs mt-1">{error}</span>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoryList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const itemsPerPage = 10;

  const filteredCategories = categories.filter((category) =>
    category.categoryName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  // Transform currentData to include displayId
  const currentData = filteredCategories
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((category, index) => ({
      ...category,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_BASE, {
          headers: {
            "x-api-key": API_KEY,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          const fetchedCategories = response.data.categories.map(
            (category) => ({
              id: category.id,
              categoryName: category.name,
            })
          );
          setCategories(fetchedCategories);
        } else {
          setError("Failed to fetch categories!");
          toast.error("Failed to fetch categories!");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.error || "Network error! Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((category) => category.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (categoryId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(categoryId)
        ? prevSelectedRows.filter((id) => id !== categoryId)
        : [...prevSelectedRows, categoryId]
    );
  };

  const handleDelete = async (categoryIds) => {
    try {
      const promises = Array.isArray(categoryIds)
        ? categoryIds.map((id) =>
            axios
              .delete(`${API_BASE}/delete/${id}`, {
                headers: {
                  "x-api-key": API_KEY,
                  "Content-Type": "application/json",
                },
              })
              .catch((error) => ({ error }))
          )
        : [
            axios
              .delete(`${API_BASE}/delete/${categoryIds}`, {
                headers: {
                  "x-api-key": API_KEY,
                  "Content-Type": "application/json",
                },
              })
              .catch((error) => ({ error })),
          ];

      const results = await Promise.allSettled(promises);
      const successfulDeletions = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled" && result.value.status === 200) {
          successfulDeletions.push(
            Array.isArray(categoryIds) ? categoryIds[index] : categoryIds
          );
        } else {
          const errorMessage =
            result.reason?.response?.data?.error ||
            `Failed to delete category with ID ${
              Array.isArray(categoryIds) ? categoryIds[index] : categoryIds
            }!`;
          toast.error(errorMessage);
        }
      });

      if (successfulDeletions.length > 0) {
        setCategories(
          categories.filter(
            (category) => !successfulDeletions.includes(category.id)
          )
        );
        setSelectedRows(
          selectedRows.filter((id) => !successfulDeletions.includes(id))
        );
        toast.success(
          `${successfulDeletions.length} category(s) deleted successfully!`
        );
      } else if (results.every((result) => result.status === "rejected")) {
        toast.error("Failed to delete any categories!");
      }
    } catch (error) {
      toast.error("Network error! Please try again.");
    }
  };

  const handleEdit = (category) => {
    setCategoryToEdit(category);
    setEditModalOpen(true);
  };

  const handleUpdateCategory = async (id, updatedName) => {
    try {
      const response = await axios.put(
        `${API_BASE}/edit/${id}`,
        { name: updatedName },
        {
          headers: {
            "x-api-key": API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setCategories(
          categories.map((category) =>
            category.id === id
              ? { ...category, categoryName: updatedName }
              : category
          )
        );
        setEditModalOpen(false);
      } else {
        toast.error("Failed to update category!");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Network error! Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleAddCategory = (newCategories) => {
    const formattedCategories = newCategories.map((category) => ({
      id: category.id,
      categoryName: category.name,
    }));
    setCategories([...formattedCategories, ...categories]);
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["Database ID", "Category Name"];
  const dataMapper = (item, index) =>
    [
      item.displayId ||
        (index !== undefined
          ? (currentPage - 1) * itemsPerPage + index + 1
          : item.id),
      item.categoryName || "",
    ].join("\t");

  // Define skeleton columns to match table structure
  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left print-hidden-in-print",
      skeletonClassName: "my-5 mx-3",
    },
    {
      width: "100px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "200px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-right p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
  ];

  return (
    <div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Medicine Category List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by category name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={() => setAddModalOpen(true)}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Medicine Category
              </button>
              <ExportOptions
                data={currentData}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Medicine Category List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Database ID
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Category Name
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : error ? (
              <div className="p-5 text-center text-red-600 print-hidden">
                {error}
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">
                No categories found.
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              currentData.length > 0 &&
                              currentData.every((item) =>
                                selectedRows.includes(item.id)
                              )
                            }
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="agreement"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Database ID
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Category Name
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((category, index) => {
                      // console.log("Category row:", {
                      //   displayId: category.displayId,
                      //   id: category.id,
                      //   categoryName: category.categoryName,
                      // });
                      return (
                        <tr
                          key={category.id}
                          className="border-b hover:bg-gray-50"
                        >
                          <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                            <div className="relative left-3 -top-[3px]">
                              <input
                                type="checkbox"
                                id={`category-${index}`}
                                className="hidden peer print-hidden"
                                checked={selectedRows.includes(category.id)}
                                onChange={() => handleRowSelect(category.id)}
                                disabled={loading}
                              />
                              <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                                <FaCheck className="text-slate-100" />
                              </span>
                              <label
                                htmlFor={`category-${index}`}
                                className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                              ></label>
                            </div>
                          </td>
                          <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {category.displayId}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {category.categoryName}
                          </td>
                          <td className="p-3 text-right space-x-2 print-hidden-in-print">
                            <button
                              onClick={() => handleEdit(category)}
                              className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                              disabled={loading}
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRows([category.id]);
                                setDeleteModalOpen(true);
                              }}
                              className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                              disabled={loading}
                              title="Delete"
                            >
                              <RiDeleteBin6Line />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={filteredCategories.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="category"
        title="Confirm Delete"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} category(s)?`
            : `Do you really want to delete this category?`
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <AddCategoryModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddCategory}
      />
      <EditCategoryModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleUpdateCategory}
        category={categoryToEdit}
      />
    </div>
  );
};

export default CategoryList;
